import React from 'react';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import { InkindOrganizerSettings, Organizer } from '@gik/core/models/gik/InkindPage';
import Checkbox from '@gik/ui/Checkbox';
import { Button, SvgIcon, Tooltip, UI } from '@gik/ui';
import { inviteUserAsPageOrganizer } from '@gik/inkind-page';
import { getCDNAssetsUrl } from '@gik/core/utils/LinkUtils';
import { defaultUserImagePath } from '@gik/core/constants';
import bemBlock from '@gik/core/utils/bemBlock';
import twConfig from '@gik/core/tailwind.config';

export const baseOrganizerItemBlockName = 'organizers-list-item';

export interface IBaseOrganizerItemProps {
  isOwner: boolean;
  isInvitee: boolean;
  className?: string;
  organizer: Organizer;
  onUpdateSettings: (isOwner: boolean, userId: string, settings: InkindOrganizerSettings) => Promise<void>;
  onRemoveOrganizer: (userId: string) => void;
  onCancelInvitation: (email: string) => void;
  routeId: string;
}

export function BaseOrganizerItem({
  isOwner,
  isInvitee,
  className,
  organizer,
  onUpdateSettings,
  onRemoveOrganizer,
  onCancelInvitation,
  routeId,
}: React.PropsWithChildren<IBaseOrganizerItemProps>): JSX.Element {
  const [canEdit, setCanEdit] = React.useState<boolean>(false);
  const [notifications, setNotifications] = React.useState<boolean>(false);

  const { t } = useTranslation();
  const bem = bemBlock(baseOrganizerItemBlockName);

  React.useEffect(() => {
    setCanEdit(organizer.canEdit);
    setNotifications(organizer.notifications);
  }, [organizer]);

  const onClickCanEdit = async (canEdit: boolean) => {
    if (!isOwner) {
      await onUpdateSettings(isOwner, organizer.userId, {
        canEdit,
        notifications,
      });
    }
  };

  const onClickNotifications = async (notifications: boolean) => {
    await onUpdateSettings(isOwner, organizer.userId, {
      canEdit,
      notifications,
    });
  };

  const onClickRemove = async (e: React.MouseEvent) => {
    if (!isOwner) {
      e.preventDefault();
      // TODO: update messages
      const message = isInvitee
        ? `Cancel organizer invitation for ${organizer.email}?`
        : `Remove ${organizer.name} as a page organizer?`;
      const title = isInvitee ? 'Remove invetee' : 'Remove organizer';
      const response = await UI.confirm(message, {
        title,
        padding: true,
      });

      if (response) {
        if (isInvitee) {
          onCancelInvitation(organizer.email);
        } else {
          onRemoveOrganizer(organizer.userId);
        }
      }
    }
  };

  // no need for optimistic update, background call with no visible effect
  const onResendClick = async (e: React.MouseEvent, organizer: Organizer) => {
    e.preventDefault();

    await inviteUserAsPageOrganizer(routeId, {
      emailAddress: organizer.email,
      canEdit: organizer.canEdit,
      notifications: organizer.notifications,
    });

    UI.success('Email Resent Successfully.');
  };

  return (
    <section className={bem(null, null, className)}>
      <div className={bem('info')}>
        <div
          className={bem('img-circle')}
          style={{
            backgroundImage: `url('${
              isInvitee || !organizer.imgSrc ? getCDNAssetsUrl() + defaultUserImagePath : organizer.imgThumbSmall
            }')`,
          }}
        />
        <div className={bem('info-name')}>
          <div className={bem('name')}>{isInvitee ? organizer.email : organizer.name}</div>
          {isOwner && <div className={bem('indicator')}>{t(translationKeys.pageOwner)}</div>}
          {isInvitee && (
            <div className={bem('invited')}>
              <span className={bem('invited-label')}>{t(translationKeys.invited)}</span>
              <Button variant="primary-link" className={bem('resend-link')} onClick={e => onResendClick(e, organizer)}>
                {t(translationKeys.resend)}
              </Button>
            </div>
          )}
        </div>
      </div>
      {!isInvitee && (
        <>
          <div className={bem('action-cb-1')}>
            <Checkbox disabled={isOwner} checked={isOwner || canEdit} onValueChange={onClickCanEdit} />
          </div>
          <div className={bem('action-cb-2')}>
            <Checkbox checked={notifications} onValueChange={onClickNotifications} />
          </div>
        </>
      )}
      <div className={bem('action-trash')}>
        <Tooltip
          allowOnMobile={true}
          disabled={!isOwner}
          zIndex={twConfig.theme.extend.zIndex.popovers}
          text={t(translationKeys.tooltip)}
        >
          <span>
            <SvgIcon className={bem('icon', [{ disabled: isOwner }])} name="TrashOutline" onClick={onClickRemove} />
          </span>
        </Tooltip>
      </div>
    </section>
  );
}

export interface IOrganizerListItemProps {
  className?: string;
  organizer: Organizer;
  onUpdateSettings: (isOwner: boolean, userId: string, settings: InkindOrganizerSettings) => Promise<void>;
  onRemoveOrganizer: (userId: string) => void;
  onCancelInvitation: (email: string) => void;
  routeId: string;
}

export function OwnerListItem(props: React.PropsWithChildren<IOrganizerListItemProps>): JSX.Element {
  return <BaseOrganizerItem isOwner={true} isInvitee={false} {...props} />;
}

export function OrganizerListItem(props: React.PropsWithChildren<IOrganizerListItemProps>): JSX.Element {
  return <BaseOrganizerItem isOwner={false} isInvitee={false} {...props} />;
}

export function InviteeListItem(props: React.PropsWithChildren<IOrganizerListItemProps>): JSX.Element {
  return <BaseOrganizerItem isOwner={false} isInvitee={true} {...props} />;
}
